/* prettier-ignore */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from 'src/app/services/translation.service';
import { ToolsService } from 'src/app/services/tools.service';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { MinimizeIconComponent } from 'src/chatbot/icons/minimize-icon.component';
import { FullScreenIconComponent } from 'src/chatbot/icons/full-screen-icon.component';
import { NormalScreenIconComponent } from 'src/chatbot/icons/normal-screen-icon.component';
import { DataChatService } from 'src/chatbot/service/dataChat.service';
import { EmbedContextService } from 'src/app/services/embed-context.service';
import { ModalController } from '@ionic/angular';
import { ChatBotInfoComponent } from '../chatBotInfo/chatBotInfo.component';
import { SendMailComponent } from '../sendMail/sendMail.component';
import { ReloadIconComponent } from 'src/chatbot/icons/reload-icon.component';
import { RouteIconComponent } from 'src/chatbot/icons/route-icon.component';
import { SendMessageService } from 'src/chatbot/service/sendMessage.service';
import { WebSocketService } from 'src/chatbot/service/webSocket.service';
import { StatisticsService } from 'src/app/services/statistics.service';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { EditRouteService } from 'src/app/pages/custom-route/shared/planner/components/route-timeline/components/daily-route-list/services';
import { WebEntryTrackingService } from 'src/app/services/web-entry-tracking.service';

@Component({
  selector: 'chat-header',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MinimizeIconComponent,
    FullScreenIconComponent,
    NormalScreenIconComponent,
    ReloadIconComponent,
    RouteIconComponent,
  ],
  templateUrl: './chatHeader.component.html',
  styleUrl: './chatHeader.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatHeaderComponent {
    private statisticsService = inject(StatisticsService)
    private webSocketService = inject(WebSocketService)
    private webEntryTracking = inject(WebEntryTrackingService)
    private router = inject(Router)
    private breakpointsService = inject(BreakpointsService)
    public toolsService = inject(ToolsService)
    public dataChatService = inject(DataChatService);
    private _embedContext = inject(EmbedContextService);
    private _customRouteService = inject(CustomRouteService);
    private _editRouteService = inject(EditRouteService);
    private _sendMessage = inject(SendMessageService);
    private _translationService = inject(TranslationService);

    // HRS-2513 (closes HRS-2504): "make a route" CTA icon in the chat header. Shown only when the
    // tenant has the planner enabled — gated by the CMS flag showPlannerButton, mirroring how
    // capabilities.voice gates the mic. Fires the same funnel as typing "hazme una ruta": sends a
    // predefined message via the WebSocket, exactly like the recommended-message CTAs.
    public showRouteCta = computed(() => this.toolsService.showPlannerButton());
    requestRoute(){ this._sendMessage.sendMessageWebSocket(this._translationService.instant('Quiero hacer una ruta')); }
    // Embed: with a route/planner, one toggle button = maximize (panel → true full screen)
    // ⇄ restore (full → panel); plus the minimize line = close to the bubble. Site untouched.
    public isEmbed = this._embedContext.isEmbed;
    // Toggle appears only with a route/planner (the panel state).
    public routeReady = computed(() => !!this._customRouteService.token());
    // True browser-fullscreen on/off (the loader/host resizes the iframe to 100vw×100vh).
    public isTrueFull = signal(false);
    public logoSrc = computed(() => this.dataChatService.icon())
    public chatName = computed(() => this.dataChatService.name())
    public isFullScreen = computed(() => this.dataChatService.isFullScreen())
    public canSaveChat = computed(() => this.dataChatService.canSaveChat())
    // HRS-2384/B5: on mobile the embed is already full-screen, so the maximize/restore
    // control is meaningless — hide it (minimize-to-bubble stays).
    public isMobile = this.breakpointsService.isMobile;

    public showMinimize = computed(() => {
      const isCustomRouteV3 = this.router.url.includes('custom-route/v3');
      if (!isCustomRouteV3) return true;
      return !this.breakpointsService.isMobile();
    })

    public showMaximizeMinimizeButton = computed(() => this._embedContext.showMaximizeMinimizeButton());

    private modalController = inject(ModalController)
    private modal: any;

    toggleView(){
        this.dataChatService.hideChat.update(prev => !prev);
        if (this.dataChatService.hideChat())
          this.dataChatService.isFullScreen.set(false)
    }

    /**
     * HRS-2737 (a11y IU-06): Esc cierra/minimiza el panel del chat, el patrón esperado en
     * diálogos. A nivel de documento porque al abrir el chat el foco no entra al panel
     * (no hay autofocus), así que un listener en el host no dispararía en la auditoría.
     * El autocomplete de lugares ya corta su Esc con stopPropagation, no llega aquí.
     */
    @HostListener('document:keydown.escape')
    async minimizeOnEscape(){
      // Con un modal del chat abierto (info / enviar email), Esc pertenece al modal.
      if (await this.modalController.getTop()) return;

      if (this.isEmbed()) {
        this.minimizeToBubble();
        return;
      }

      // showMinimize() false = el chat es una pestaña del planner móvil, no hay "minimizar".
      if (!this.showMinimize() || this.dataChatService.hideChat()) return;
      this.toggleView();
    }

    resetChat(){
      // Embed (HRS-2384): the ↻ button is what users reach for to "start over". If a
      // route/planner is open, close it too so we land on a clean chat — the planner
      // doesn't auto-reopen, and the iframe is restored to its normal chat size (the route
      // had asked the parent to maximize it; 'minimizeChatbot' is the loader's restore
      // counterpart). The site has no inline planner, so this is embed-only.
      if (this.isEmbed() && this.routeReady()) {
        if (this.isTrueFull()) {
          this.isTrueFull.set(false);
          try { window.parent?.postMessage('exitFullscreen', '*'); } catch {}
        }
        try { window.parent?.postMessage('minimizeChatbot', '*'); } catch {}
        this._customRouteService.reset();
        // Also clear the edited route: route_active in the WS payload is driven by
        // EditRouteService.editedCustomRouteData, so without this the backend still
        // thinks a route is in progress ("tienes una ruta en curso") after the reset.
        this._editRouteService.resetEditState();
      }
      this.dataChatService.abortSignal()?.abort();
      this.webSocketService.cancelCompletion();
      this.webSocketService.disconnect();
      const newToken = this.statisticsService.generateNewSessionId();
      this.dataChatService.token.set(newToken);
      this.dataChatService.messages.set([])
      // Reset the conversation to its initial state so the welcome CTAs reappear
      // (hasHistory stays "true" once a message is sent; clearing messages alone won't bring them back).
      this.dataChatService.hasHistory.set(false);
      this.webEntryTracking.rebindToChatThread();
      // HRS-3045 (bloque 4): reconexión capability-driven (no reabrir sockets que nadie consume)
      this.webSocketService.connectIfNeeded();
    }

    toggleFullScreen(){
      this.dataChatService.isFullScreen.update(prev => !prev);
    }

    /**
     * Embed maximize toggle: panel ("con planner") ⇄ true full screen. Same button both
     * ways; the icon flips with isTrueFull. The parent loader/host resizes the iframe, so
     * we post enterFullscreen / exitFullscreen.
     */
    toggleTrueFull(){
      const next = !this.isTrueFull();
      this.isTrueFull.set(next);
      try { window.parent?.postMessage(next ? 'enterFullscreen' : 'exitFullscreen', '*'); } catch {}
    }

    /**
     * Embed minimize line → collapse the widget to the floating bubble. The production loader
     * (chatbot-script-v3.js) handles this as 'hideChatbot' (→ toggle() hides the iframe and
     * shows its own launcher button, which reopens it). Must match that exact message.
     * In standalone (no parent) it's a no-op.
     */
    minimizeToBubble(){
      this.isTrueFull.set(false);
      try { window.parent?.postMessage('hideChatbot', '*'); } catch {}
    }

    toggleAction(action: string){
      switch(action){
        case 'email': 
          if (!this.canSaveChat()) return;
          this.openModal(SendMailComponent); 
          break;
        case 'info': this.openModal(ChatBotInfoComponent); break;
      }
    }

    async openModal(component: any) {
      if (this.modal) return;
      this.modal = await this.modalController.create({
        component: component,
        // CMAD-231: sin 'ion-disable-focus-trap' (misma clase de defecto que chatInputForm.openModal;
        // hoy sin llamadores en plantilla).
        cssClass: ['chatBotModal'],
        componentProps: {},
      });
      await this.modal.present();
      await this.modal.onDidDismiss();
      this.modal = null;
    }

    
}